import { useEffect, useState } from "react";
import { Team, TeamData } from "types/realization";
import { BiSolidTrash } from "react-icons/bi";
import { IoCheckmarkCircle } from "react-icons/io5";
import { HiStar } from "react-icons/hi";
import { useModal } from "context/ModalContext/Modal.context";
import { clearRegistrationUser } from "utils/fns";
import ModalWarning from "../ModalWarning";
import Filter from "../Filter";
import NavButton from "../Button/NavButton";

interface Options {
  options: Array<TeamData>;
  selected?: Array<any>;
  limitSelected: number;
  minSelected: number;
  onSelected: (val?: TeamData[]) => void;
  onBack: () => void;
  onNext: () => void;
  isNextDisabled: boolean;
  team: Team[];
  nextButtonText?: string;
  rankingMap?: Map<string, { points: number; posicao: number }>;
}

interface OptionItem {
  currentParticipant: TeamData;
  allowRemove?: boolean;
}

const Participants = ({
  options,
  selected = [],
  limitSelected,
  minSelected,
  onSelected,
  onBack,
  onNext,
  team,
  isNextDisabled,
  nextButtonText = "Finalizar Inscrição",
  rankingMap,
}: Options) => {
  const { showModal, closeModal } = useModal();
  const [participantOptions, setParticipantOptions] = useState<TeamData[]>([]);
  const [currentSelected, setCurrentSelected] = useState<TeamData[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (options.length > 0) {
      setParticipantOptions(options);
      if (options.length <= limitSelected) {
        setCurrentSelected(options);
      }
    }
  }, [options]);

  useEffect(() => {
    if (!!searchTerm && searchTerm.length >= 0) {
      let newList = participantOptions
        .map((item) => ({
          ...item,
          searchField:
            item.searchField ||
            `${item._id} ${item.chave} ${item.nome} ${item.funcao} ${item.nome_dependencia} ${item.prefixo}`,
        }))
        .filter(
          (item) =>
            !currentSelected.some((selected) => selected._id === item._id)
        )
        .filter((item) => {
          return item.searchField
            .toUpperCase()
            .includes(searchTerm.toUpperCase());
        });

      setParticipantOptions(newList);
    } else {
      setParticipantOptions(
        options.filter(
          (item) =>
            !currentSelected.some((selected) => selected._id === item._id)
        )
      );
    }
  }, [searchTerm]);

  useEffect(() => {
    if (!!selected && selected.length > 0 && options.length > 0) {
      const selectedIds = selected.map((item) => item.player);
      const selectedConcorrents = options.filter((item) =>
        selectedIds.includes(item._id)
      );

      setCurrentSelected(selectedConcorrents);

      const newListParticipants = options.filter(
        (item) => !selectedIds.includes(item._id)
      );

      if (!!newListParticipants && newListParticipants.length > 0) {
        setParticipantOptions(newListParticipants);
      }
    }
  }, [selected, options]);

  useEffect(() => {
    onSelected(currentSelected);
  }, [currentSelected]);

  const selectParticipant = (participant: TeamData) => {
    if (currentSelected.length >= limitSelected) {
      console.log("is limited");
      showModal(
        "limit-participants",
        <ModalWarning
          title={`Você já indicou ${limitSelected} participantes! `}
          description="Para selecionar outra pessoa, é necessário desmarcar uma selecionadas anteriormente."
          btnText="Continuar"
          onClose={closeModal}
        />,
        "participant-limit"
      );
    } else {
      const isSelected = currentSelected.findIndex(
        (item) => item._id === participant._id
      );

      if (isSelected === -1) {
        let updatedList = participantOptions.filter(
          (item) => item._id !== participant._id
        );

        setCurrentSelected([...currentSelected, participant]);

        setParticipantOptions(updatedList);
      }
    }
  };

  const removeParticipant = (participant: TeamData) => {
    const updatedSelection = currentSelected
      .filter((p) => p._id !== participant._id)
      .sort((a, b) =>
        a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" })
      );
    setCurrentSelected(updatedSelection);

    setParticipantOptions((prev) =>
      [...prev, participant].sort((a, b) =>
        a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" })
      )
    );
  };

  const CardParticipant = ({ currentParticipant, allowRemove }: OptionItem) => {
    const rankingInfo = rankingMap?.get(
      clearRegistrationUser(currentParticipant._id),
    );

    return (
      <div className="rounded-lg bg-white-bb-1 px-4 py-4 border-1 border-b-2 border-[#d4d8dd]">
        <div className="flex justify-between items-start gap-3">
          {rankingInfo ? (
            <div className="inline-flex items-center gap-2 bg-yellow-bb-1 text-blue-bb-1 rounded-full px-3 py-1 text-xs font-extrabold uppercase tracking-wide">
              <HiStar size={14} />
              <span>
                #{rankingInfo.posicao} no ranking
              </span>
              <span className="text-blue-bb-1/80 normal-case font-semibold">
                ({rankingInfo.points.toFixed(1)} pts)
              </span>
            </div>
          ) : (
            <span />
          )}
          {allowRemove ? (
            <button
              className="flex items-center gap-2 text-xs font-bold uppercase md:text-sm text-blue-bb-2"
              onClick={() => removeParticipant(currentParticipant)}
            >
              Remover participante{" "}
              <BiSolidTrash color={"#3354FD"} size={18} />
            </button>
          ) : (
            <button
              type="button"
              className="flex items-center gap-2 text-xs font-bold uppercase md:text-sm text-blue-bb-2"
              onClick={() => selectParticipant(currentParticipant)}
              aria-label={`Selecionar participante ${currentParticipant.nome}`}
            >
              <span>Selecionar participante</span>
              <IoCheckmarkCircle size={16} color="#3354FD" aria-hidden="true" />
            </button>
          )}
        </div>

        <h3 className="text-lg font-bold text-black-bb font-title mt-2">
          {currentParticipant.nome} ({currentParticipant.chave})
          <span className="text-xs font-bold text-gray-bb-2">
            | {currentParticipant.funcao} - {currentParticipant.prefixo} -{" "}
            {currentParticipant.nome_dependencia}
          </span>
        </h3>
        {team.some((t) => t.user_id === currentParticipant._id && t.message) && (
          <div className="p-4 text-sm bg-gray-bb-3 rounded-[4px] mt-2">
            <p className="font-medium text-gray-bb-2 font-bb">
              <strong className="text-black-bb">
                Atuação do funcionário na entrega:
              </strong>{" "}
                <div
                dangerouslySetInnerHTML={{
                  __html: team.find((t) => t.user_id === currentParticipant._id)?.message || "",
                }}
                />
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="mx-4 mb-0 md:mb-16 md:mx-0">
        <h2
          className="text-gray-bb-1 font-title text-xl md:text-[26px] mb-8 font-bold"
          onClick={() => {
            console.log("Participant Options:", participantOptions);
          }}
        >
          Participantes selecionados ({currentSelected.length}/{limitSelected})
        </h2>
        {currentSelected.length === 0 && (
          <p>Você ainda não selecionou nenhum participante.</p>
        )}
        {currentSelected?.map((item) => (
          <div key={item._id} className="mb-6">
            <CardParticipant currentParticipant={item} allowRemove={true} />
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-4 m-4 actions md:gap-8 md:my-8">
        <NavButton text="Voltar" bgColor="#E0E9FF" onClick={onBack} />
        <NavButton
          text={nextButtonText}
          bgColor="#FCFC30"
          disabled={isNextDisabled}
          onClick={onNext}
        />
      </div>

      <div className="mx-4 mb-16 md:mx-0">
        <div className="flex flex-col justify-between">
          <h2 className="text-gray-bb-1 font-title text-[26px] mb-8 font-bold flex-1">
            Participantes da realização não selecionados (
            {participantOptions.length})
          </h2>
          <div className="flex-1">
            <Filter
              enableCategoryFilter={false}
              inEnd={true}
              placeholderField="Buscar participante"
              onChange={(key, value) => {
                setSearchTerm(value);
              }}
            />
          </div>
        </div>
        {participantOptions.map((item) => (
          <div key={item._id} className="mb-6">
            <CardParticipant currentParticipant={item} allowRemove={false} />
          </div>
        ))}
      </div>
    </>
  );
};

export default Participants;
